import React from 'react'
import PropTypes from 'prop-types'

/* doc
---
title: MenuList
name: MenuList
category: React - Bulma
parent: Menu
---

A `MenuList` component based on Bulma `.menu-list`. This is used to add a
list of links to the `Menu` component. Children of this component should be
`<li>` elements.

This should be used as a child of the `Menu` component.

See [Bulma Documentation](http://bulma.io/documentation/components/menu/) for reference and full options.
If one of the Bulma options isn't listed below, it may be added using the `className` prop.

### Customizations

In addition to the Bulma options. You can also add `isHorizontal` to turn the list into a horizontally
displayed list (this is good for a `Menu` with just a `MenuList` inside it.)

If `isHorizontal` is set, you can also include `isDivided` to add a divider between link items.

Also with `isHorizontal` if you need to pull any wrapped link items to the right you can add `isJustifiedRight`.

### Props
| Prop Name        | Description                                 |Type            | Default Value             | Required?     |
|------------------|---------------------------------------------|----------------|---------------------------|---------------|
| isHorizontal     | display items side-by-side                  | boolean        | false                     | no            |
| isDivided        | Adds a divider between menu items           | boolean        | false                     | no            |
| isJustifiedRight | Pulls wrapped menu items to the right       | boolean        | false                     | no            |

```html_example
  <Menu>
    <MenuList isHorizontal>
      <li><Link href="/legal">Legal</Link></li>
      <li><Link href="/jobs">Jobs</Link></li>
    </MenuList>
  </Menu>
```
*/
function MenuList(props) {
  const {
    children, className, isDivided, isHorizontal, isJustifiedRight, ...rest
  } = props

  const classes = [
    'menu-list',
    className ? props.className : '',
    isHorizontal ? 'is-horizontal' : '',
    isDivided ? 'is-divided' : '',
    isJustifiedRight ? 'has-right-justified-items' : '',
  ]

  return (
    <ul {...rest} className={classes.join(' ')}>
      {children}
    </ul>
  )
}

MenuList.defaultProps = {
  isDivided: false,
  isHorizontal: false,
  isJustifiedRight: false,
}

MenuList.propTypes = {
  // Adds a delimiter between menu items.
  isDivided: PropTypes.bool,
  // Menu items are side-by-side.
  isHorizontal: PropTypes.bool,
  // Menu items are justified to the right when wrapped.
  isJustifiedRight: PropTypes.bool,
}

export default MenuList
